<script setup lang="ts">
// Sierra's .chip, three ways:
// - static: a label with an optional mono `value` (`<b>`);
// - a filter (`pressed` set): a 38 px button with aria-pressed and an optional mono
//   `count`, that emits `toggle`;
// - `removable`: with a × that emits `remove`.
// An explicit undefined default: Vue would otherwise cast a missing boolean `pressed` to false.
withDefaults(defineProps<{ label: string; value?: string | number | undefined; count?: number | undefined; pressed?: boolean | undefined; removable?: boolean }>(), {
  pressed: undefined,
  removable: false,
});
const emit = defineEmits<{ toggle: []; remove: [] }>();
</script>

<template>
  <button v-if="pressed !== undefined" type="button" class="itc-chip itc-chip--filter" :aria-pressed="pressed" @click="emit('toggle')">
    {{ label }}<b v-if="count !== undefined">{{ count }}</b>
  </button>
  <span v-else class="itc-chip" :class="{ 'itc-chip--removable': removable }">
    {{ label }}<b v-if="value !== undefined">{{ value }}</b>
    <button v-if="removable" type="button" class="itc-chip__remove" :aria-label="`Remove ${label}`" @click="emit('remove')">×</button>
  </span>
</template>

<style scoped>
.itc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid var(--itc-line);
  border-radius: var(--itc-r-pill);
  background: var(--itc-panel-2);
  color: var(--itc-fg);
  font-size: 13px;
  white-space: nowrap;
}
.itc-chip b {
  font-family: var(--itc-font-mono);
  font-weight: 500;
}
.itc-chip--filter {
  min-height: 38px;
  padding: 0 14px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.itc-chip--filter b {
  color: var(--itc-muted);
}
.itc-chip--filter[aria-pressed='true'] {
  border-color: var(--itc-accent);
  background: var(--itc-accent-soft);
}
.itc-chip--removable {
  padding-right: 4px;
}
.itc-chip__remove {
  min-width: 30px;
  min-height: 30px;
  margin: -4px 0;
  border: 0;
  border-radius: var(--itc-r-pill);
  background: none;
  color: var(--itc-muted);
  font-size: 17px;
  line-height: 1;
  cursor: pointer;
}
.itc-chip__remove:hover {
  color: var(--itc-fg);
}
</style>
